import { useState } from 'react';
import Header from '../home/components/Header';
import Footer from '../home/components/Footer';

export default function EnterpriseSoftwareImplementationPage() {
  const [selectedOptions, setSelectedOptions] = useState<{ [key: string]: string }>({
    'company-size': '',
    'industry': '',
    'timeline': '',
    'budget': ''
  });

  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [openFAQ, setOpenFAQ] = useState<string | null>(null);
  const [formSubmitted, setFormSubmitted] = useState(false);

  const toggleDropdown = (dropdownId: string) => {
    setOpenDropdown(openDropdown === dropdownId ? null : dropdownId);
  };

  const selectOption = (dropdownId: string, value: string) => {
    setSelectedOptions(prev => ({ ...prev, [dropdownId]: value }));
    setOpenDropdown(null);
  };

  const toggleFAQ = (faqId: string) => {
    setOpenFAQ(openFAQ === faqId ? null : faqId);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    // Add selected dropdown values
    Object.entries(selectedOptions).forEach(([key, value]) => {
      if (value) {
        formData.append(key.replace('-', '_'), value);
      }
    });

    try {
      const response = await fetch('https://readdy.ai/api/form/d50nqplmdo3ncn62h7ng', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData as any).toString(),
      });

      if (response.ok) {
        setFormSubmitted(true);
        form.reset();
        setSelectedOptions({
          'company-size': '',
          'industry': '',
          'timeline': '',
          'budget': ''
        });
      }
    } catch (error) {
      console.error('Form submission error:', error);
    }
  };

  return (
    <div className="bg-white min-h-screen">
      <Header />

      {/* Breadcrumb */}
      <div className="bg-gray-50 py-4">
        <div className="container mx-auto px-4">
          <nav className="flex items-center space-x-2 text-sm">
            <a href="/" className="text-gray-600 hover:text-primary">Home</a>
            <div className="w-4 h-4 flex items-center justify-center text-gray-400">
              <i className="ri-arrow-right-s-line"></i>
            </div>
            <a href="#" className="text-gray-600 hover:text-primary">IT Solutions</a>
            <div className="w-4 h-4 flex items-center justify-center text-gray-400">
              <i className="ri-arrow-right-s-line"></i>
            </div>
            <span className="text-gray-900 font-medium">Enterprise Software Implementation</span>
          </nav>
        </div>
      </div>

      {/* Hero Section */}
      <section className="relative min-h-[600px] flex items-center" style={{
        backgroundImage: 'url(https://static.readdy.ai/image/2f7102ba1bcef2b8369bef774bca35e3/18402ea34661098911b5e45aca67003f.png)',
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }}>
        <div className="absolute inset-0 bg-gradient-to-r from-white via-white/95 to-transparent"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="w-full max-w-2xl">
            <h1 className="text-5xl font-bold text-gray-900 mb-6 leading-tight">
              Enterprise Software Implementation Solutions
            </h1>
            <p className="text-xl text-gray-700 mb-8 leading-relaxed">
              Transform your business operations with our comprehensive enterprise software implementation services. From initial assessment to full deployment and ongoing support, we ensure seamless integration that drives efficiency and growth.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <button
                onClick={() => document.getElementById('consultation-form')?.scrollIntoView({ behavior: 'smooth' })}
                className="bg-primary text-white px-8 py-4 rounded-button whitespace-nowrap font-semibold text-lg hover:bg-primary/90 transition-colors inline-flex items-center justify-center cursor-pointer"
              >
                <div className="w-5 h-5 flex items-center justify-center mr-2">
                  <i className="ri-calendar-check-line"></i>
                </div>
                Schedule Consultation
              </button>
              <button className="border-2 border-primary text-primary px-8 py-4 rounded-button whitespace-nowrap font-semibold text-lg hover:bg-primary hover:text-white transition-colors inline-flex items-center justify-center cursor-pointer">
                <div className="w-5 h-5 flex items-center justify-center mr-2">
                  <i className="ri-download-line"></i>
                </div>
                Download Brochure
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Comprehensive Implementation Services */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">Comprehensive Implementation Services</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our enterprise software implementation approach combines industry expertise with proven methodologies to deliver solutions that align perfectly with your business objectives.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center p-6">
              <div className="w-16 h-16 bg-primary/10 rounded-xl flex items-center justify-center mx-auto mb-6">
                <div className="w-8 h-8 flex items-center justify-center text-primary">
                  <i className="ri-settings-3-line ri-2x"></i>
                </div>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Seamless Integration</h3>
              <p className="text-gray-600">
                Connect new systems with existing infrastructure without disrupting daily operations.
              </p>
            </div>
            <div className="text-center p-6">
              <div className="w-16 h-16 bg-green-50 rounded-xl flex items-center justify-center mx-auto mb-6">
                <div className="w-8 h-8 flex items-center justify-center text-green-600">
                  <i className="ri-time-line ri-2x"></i>
                </div>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Reduced Downtime</h3>
              <p className="text-gray-600">
                Minimize business disruption with our strategic implementation timeline and rollback procedures.
              </p>
            </div>
            <div className="text-center p-6">
              <div className="w-16 h-16 bg-blue-50 rounded-xl flex items-center justify-center mx-auto mb-6">
                <div className="w-8 h-8 flex items-center justify-center text-blue-600">
                  <i className="ri-user-star-line ri-2x"></i>
                </div>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Expert Guidance</h3>
              <p className="text-gray-600">
                Leverage our certified professionals with deep expertise in enterprise software solutions.
              </p>
            </div>
            <div className="text-center p-6">
              <div className="w-16 h-16 bg-purple-50 rounded-xl flex items-center justify-center mx-auto mb-6">
                <div className="w-8 h-8 flex items-center justify-center text-purple-600">
                  <i className="ri-customer-service-2-line ri-2x"></i>
                </div>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Ongoing Support</h3>
              <p className="text-gray-600">
                Comprehensive post-implementation support to ensure long-term success and optimization.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Implementation Process */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">Our Implementation Process</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              A proven six-phase methodology that ensures successful enterprise software deployment from conception to completion.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                number: '1',
                title: 'Assessment & Planning',
                description: 'Comprehensive analysis of current systems, business requirements, and implementation roadmap development.',
                timeline: 'Timeline: 2-4 weeks'
              },
              {
                number: '2',
                title: 'System Design',
                description: 'Architecture design, integration mapping, and customization planning tailored to your specific needs.',
                timeline: 'Timeline: 3-6 weeks'
              },
              {
                number: '3',
                title: 'Development & Configuration',
                description: 'Custom development, system configuration, and integration setup in controlled environments.',
                timeline: 'Timeline: 6-12 weeks'
              },
              {
                number: '4',
                title: 'Testing & Quality Assurance',
                description: 'Rigorous testing protocols including unit testing, integration testing, and user acceptance testing.',
                timeline: 'Timeline: 2-4 weeks'
              },
              {
                number: '5',
                title: 'Deployment',
                description: 'Phased rollout with minimal disruption, data migration, and go-live support with dedicated teams.',
                timeline: 'Timeline: 1-3 weeks'
              },
              {
                number: '6',
                title: 'Ongoing Support',
                description: 'Continuous monitoring, performance optimization, user training, and technical support services.',
                timeline: 'Timeline: Ongoing'
              }
            ].map((phase, index) => (
              <div key={index} className="bg-white rounded-xl p-8 shadow-sm border border-gray-100 relative">
                <div className="absolute -top-4 left-8">
                  <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center text-white font-bold text-sm">
                    {phase.number}
                  </div>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4 mt-4">{phase.title}</h3>
                <p className="text-gray-600 mb-4">{phase.description}</p>
                <div className="text-sm text-primary font-medium">{phase.timeline}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Supported Enterprise Platforms */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">Supported Enterprise Platforms</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We specialize in implementing leading enterprise software platforms across all major business functions, ensuring seamless integration and optimal performance.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: 'ri-database-2-line',
                color: 'blue',
                title: 'ERP Systems',
                description: 'Comprehensive enterprise resource planning solutions for streamlined operations.',
                items: ['SAP S/4HANA', 'Oracle ERP Cloud', 'Microsoft Dynamics 365', 'NetSuite ERP', 'Infor CloudSuite']
              },
              {
                icon: 'ri-customer-service-2-line',
                color: 'green',
                title: 'CRM Platforms',
                description: 'Customer relationship management systems to enhance sales and customer service.',
                items: ['Salesforce CRM', 'HubSpot CRM', 'Microsoft Dynamics CRM', 'Zoho CRM', 'Pipedrive']
              },
              {
                icon: 'ri-bar-chart-box-line',
                color: 'purple',
                title: 'Business Intelligence',
                description: 'Advanced analytics and reporting solutions for data-driven decision making.',
                items: ['Microsoft Power BI', 'Tableau', 'QlikView / QlikSense', 'IBM Cognos Analytics', 'Looker']
              },
              {
                icon: 'ri-folder-shared-line',
                color: 'orange',
                title: 'Enterprise Content Management',
                description: 'Document and content management systems for organized information handling.',
                items: ['SharePoint Online', 'DocuSign', 'Box Enterprise', 'OpenText', 'M-Files']
              },
              {
                icon: 'ri-shopping-cart-2-line',
                color: 'red',
                title: 'E-Commerce Platforms',
                description: 'Comprehensive e-commerce solutions for online business operations and sales.',
                items: ['Magento Commerce', 'Shopify Plus', 'WooCommerce', 'BigCommerce Enterprise', 'Salesforce Commerce']
              },
              {
                icon: 'ri-team-line',
                color: 'indigo',
                title: 'Human Capital Management',
                description: 'HR management systems for workforce optimization and employee engagement.',
                items: ['Workday HCM', 'SuccessFactors', 'BambooHR', 'ADP Workforce Now', 'UltiPro']
              }
            ].map((platform, index) => (
              <div key={index} className="bg-white rounded-xl border border-gray-200 p-8 hover:shadow-lg transition-shadow">
                <div className={`w-16 h-16 bg-${platform.color}-50 rounded-xl flex items-center justify-center mx-auto mb-6`}>
                  <div className={`w-8 h-8 flex items-center justify-center text-${platform.color}-600`}>
                    <i className={`${platform.icon} ri-2x`}></i>
                  </div>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4 text-center">{platform.title}</h3>
                <p className="text-gray-600 mb-6 text-center">{platform.description}</p>
                <ul className="space-y-3">
                  {platform.items.map((item, idx) => (
                    <li key={idx} className="flex items-center">
                      <div className="w-4 h-4 flex items-center justify-center text-green-600 mr-3">
                        <i className="ri-check-line"></i>
                      </div>
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <div className="text-center mt-12">
            <p className="text-lg text-gray-600 mb-6">
              Need implementation support for a platform not listed? Our team has experience with 200+ enterprise software solutions.
            </p>
            <button className="bg-primary text-white px-8 py-4 rounded-button whitespace-nowrap font-semibold text-lg hover:bg-primary/90 transition-colors inline-flex items-center cursor-pointer">
              <div className="w-5 h-5 flex items-center justify-center mr-2">
                <i className="ri-question-answer-line"></i>
              </div>
              Discuss Your Platform
            </button>
          </div>
        </div>
      </section>

      {/* Data Migration Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">Secure & Efficient Data Migration</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our comprehensive data migration services ensure seamless, secure, and accurate transfer of your critical business data during enterprise software implementation with minimal downtime and maximum security.
            </p>
          </div>

          {/* Migration Strategy */}
          <div className="mb-20">
            <div className="text-center mb-12">
              <h3 className="text-3xl font-bold text-gray-900 mb-4">Migration Strategy</h3>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                We develop customized migration strategies tailored to your specific business requirements, ensuring optimal performance and minimal disruption.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[
                {
                  icon: 'ri-route-line',
                  color: 'blue',
                  title: 'Phased Migration',
                  description: 'Gradual data transfer in manageable phases to minimize business impact and allow for thorough testing at each stage.',
                  features: ['Reduced risk exposure', 'Continuous validation', 'Flexible rollback options']
                },
                {
                  icon: 'ri-time-line',
                  color: 'green',
                  title: 'Big Bang Migration',
                  description: 'Complete system cutover during planned maintenance windows for faster go-live with comprehensive preparation.',
                  features: ['Shorter overall timeline', 'Single point of complexity', 'Immediate benefits realization']
                },
                {
                  icon: 'ri-arrow-left-right-line',
                  color: 'purple',
                  title: 'Parallel Migration',
                  description: 'Run old and new systems simultaneously for extended validation periods and seamless transition.',
                  features: ['Zero downtime approach', 'Extended testing period', 'Immediate fallback option']
                }
              ].map((strategy, index) => (
                <div key={index} className="bg-white rounded-xl p-8 shadow-sm border border-gray-100">
                  <div className={`w-16 h-16 bg-${strategy.color}-50 rounded-xl flex items-center justify-center mx-auto mb-6`}>
                    <div className={`w-8 h-8 flex items-center justify-center text-${strategy.color}-600`}>
                      <i className={`${strategy.icon} ri-2x`}></i>
                    </div>
                  </div>
                  <h4 className="text-xl font-semibold text-gray-900 mb-4 text-center">{strategy.title}</h4>
                  <p className="text-gray-600 text-center mb-4">{strategy.description}</p>
                  <ul className="space-y-2 text-sm text-gray-600">
                    {strategy.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center">
                        <div className="w-4 h-4 flex items-center justify-center text-green-600 mr-2">
                          <i className="ri-check-line"></i>
                        </div>
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>

          {/* Data Preparation */}
          <div className="mb-20">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <img
                  src="https://static.readdy.ai/image/2f7102ba1bcef2b8369bef774bca35e3/18402ea34661098911b5e45aca67003f.png"
                  alt="Data Preparation Process"
                  className="w-full h-80 object-cover rounded-xl shadow-lg"
                />
              </div>
              <div>
                <h3 className="text-3xl font-bold text-gray-900 mb-6">Data Preparation</h3>
                <p className="text-lg text-gray-600 mb-8">
                  Comprehensive data preparation ensures clean, consistent, and migration-ready data through systematic analysis, cleansing, and validation processes.
                </p>
                <div className="space-y-6">
                  {[
                    {
                      icon: 'ri-search-line',
                      color: 'blue',
                      title: 'Data Discovery & Profiling',
                      description: 'Comprehensive analysis of source data structures, relationships, quality issues, and business rules to create detailed migration blueprints.'
                    },
                    {
                      icon: 'ri-tools-line',
                      color: 'green',
                      title: 'Data Cleansing & Standardization',
                      description: 'Automated and manual processes to eliminate duplicates, correct inconsistencies, and standardize formats according to target system requirements.'
                    },
                    {
                      icon: 'ri-shield-check-line',
                      color: 'purple',
                      title: 'Data Quality Validation',
                      description: 'Multi-layer validation processes including completeness checks, accuracy verification, and business rule compliance testing.'
                    }
                  ].map((item, index) => (
                    <div key={index} className="flex items-start">
                      <div className={`w-12 h-12 bg-${item.color}-50 rounded-lg flex items-center justify-center mr-4 mt-1`}>
                        <div className={`w-6 h-6 flex items-center justify-center text-${item.color}-600`}>
                          <i className={`${item.icon} ri-lg`}></i>
                        </div>
                      </div>
                      <div>
                        <h4 className="text-lg font-semibold text-gray-900 mb-2">{item.title}</h4>
                        <p className="text-gray-600">{item.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Migration Execution */}
          <div className="mb-12">
            <div className="text-center mb-12">
              <h3 className="text-3xl font-bold text-gray-900 mb-4">Migration Execution</h3>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                Our proven execution methodology ensures secure, efficient, and monitored data transfer with real-time tracking and immediate issue resolution.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
              {[
                { icon: 'ri-database-2-line', color: 'blue', title: 'Pre-Execution Validation', description: 'Final system checks, connectivity testing, and readiness confirmation before migration start.' },
                { icon: 'ri-download-cloud-line', color: 'green', title: 'Data Extraction', description: 'Secure extraction from source systems with encrypted channels and integrity verification.' },
                { icon: 'ri-refresh-line', color: 'purple', title: 'Data Transformation', description: 'Real-time data transformation according to mapping rules and target system requirements.' },
                { icon: 'ri-upload-cloud-line', color: 'orange', title: 'Data Loading', description: 'Optimized loading processes with transaction management and immediate validation checks.' }
              ].map((step, index) => (
                <div key={index} className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 text-center">
                  <div className={`w-14 h-14 bg-${step.color}-50 rounded-xl flex items-center justify-center mx-auto mb-4`}>
                    <div className={`w-7 h-7 flex items-center justify-center text-${step.color}-600`}>
                      <i className={`${step.icon} ri-xl`}></i>
                    </div>
                  </div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-3">{step.title}</h4>
                  <p className="text-gray-600 text-sm">{step.description}</p>
                </div>
              ))}
            </div>

            {/* Real-Time Monitoring Dashboard */}
            <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-100">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
                <div>
                  <h4 className="text-2xl font-bold text-gray-900 mb-6">Real-Time Monitoring Dashboard</h4>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center p-4 bg-gray-50 rounded-lg">
                      <span className="text-gray-700 font-medium">Migration Progress</span>
                      <span className="text-2xl font-bold text-primary">87%</span>
                    </div>
                    <div className="flex justify-between items-center p-4 bg-gray-50 rounded-lg">
                      <span className="text-gray-700 font-medium">Records Processed</span>
                      <span className="text-2xl font-bold text-green-600">2.4M</span>
                    </div>
                    <div className="flex justify-between items-center p-4 bg-gray-50 rounded-lg">
                      <span className="text-gray-700 font-medium">Processing Speed</span>
                      <span className="text-2xl font-bold text-blue-600">15K/min</span>
                    </div>
                    <div className="flex justify-between items-center p-4 bg-gray-50 rounded-lg">
                      <span className="text-gray-700 font-medium">Error Rate</span>
                      <span className="text-2xl font-bold text-purple-600">0.02%</span>
                    </div>
                  </div>
                </div>
                <div>
                  <div className="bg-gray-50 rounded-xl p-6">
                    <h5 className="text-lg font-semibold text-gray-900 mb-4">Execution Features</h5>
                    <div className="space-y-3">
                      {[
                        '24/7 monitoring and alerts',
                        'Automated error handling',
                        'Progress tracking and reporting',
                        'Rollback capabilities',
                        'Data integrity verification',
                        'Performance optimization'
                      ].map((feature, index) => (
                        <div key={index} className="flex items-center">
                          <div className="w-5 h-5 flex items-center justify-center text-green-600 mr-3">
                            <i className="ri-check-line"></i>
                          </div>
                          <span className="text-gray-700">{feature}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Migration Approaches */}
          <div className="mb-12">
            <div className="text-center mb-12">
              <h3 className="text-3xl font-bold text-gray-900 mb-4">Migration Approaches</h3>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                Choose from multiple migration approaches based on your specific business requirements, system complexity, and downtime constraints.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {[
                {
                  icon: 'ri-cloud-line',
                  color: 'blue',
                  title: 'Cloud-to-Cloud Migration',
                  description: 'Seamless data transfer between cloud platforms with optimized bandwidth utilization and security protocols.',
                  items: ['AWS to Azure', 'Google Cloud to AWS', 'Multi-cloud consolidation']
                },
                {
                  icon: 'ri-server-line',
                  color: 'green',
                  title: 'On-Premise to Cloud',
                  description: 'Secure migration from legacy on-premise systems to modern cloud infrastructure with minimal disruption.',
                  items: ['Legacy system modernization', 'Hybrid cloud setup', 'Compliance maintenance']
                },
                {
                  icon: 'ri-database-line',
                  color: 'purple',
                  title: 'Database Migration',
                  description: 'Specialized migration services for complex database systems with zero data loss and minimal downtime.',
                  items: ['Oracle to PostgreSQL', 'SQL Server to MySQL', 'NoSQL migrations']
                },
                {
                  icon: 'ri-apps-line',
                  color: 'orange',
                  title: 'Application Migration',
                  description: 'End-to-end application and data migration with code refactoring and platform optimization.',
                  items: ['Monolith to microservices', 'Legacy app modernization', 'Container migration']
                }
              ].map((approach, index) => (
                <div key={index} className="bg-white rounded-xl p-8 shadow-sm border border-gray-100">
                  <div className={`w-16 h-16 bg-${approach.color}-50 rounded-xl flex items-center justify-center mx-auto mb-6`}>
                    <div className={`w-8 h-8 flex items-center justify-center text-${approach.color}-600`}>
                      <i className={`${approach.icon} ri-2x`}></i>
                    </div>
                  </div>
                  <h4 className="text-xl font-semibold text-gray-900 mb-4 text-center">{approach.title}</h4>
                  <p className="text-gray-600 text-center mb-6">{approach.description}</p>
                  <div className="space-y-3">
                    {approach.items.map((item, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <span className="text-gray-700">{item}</span>
                        <div className="w-5 h-5 flex items-center justify-center text-green-600">
                          <i className="ri-check-line"></i>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Security & Compliance */}
          <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-100">
            <div className="text-center mb-8">
              <h4 className="text-2xl font-bold text-gray-900 mb-4">Security & Compliance Guarantees</h4>
              <p className="text-lg text-gray-600">
                Industry-leading security measures and compliance standards throughout the migration process.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { icon: 'ri-shield-check-line', color: 'red', title: 'End-to-End Encryption', description: 'AES-256 encryption for data in transit and at rest' },
                { icon: 'ri-file-shield-line', color: 'blue', title: 'Compliance Ready', description: 'GDPR, HIPAA, SOX, and PCI-DSS compliant processes' },
                { icon: 'ri-eye-line', color: 'green', title: 'Complete Audit Trail', description: 'Detailed logging and monitoring of all migration activities' },
                { icon: 'ri-history-line', color: 'purple', title: 'Instant Recovery', description: 'Automated backup and rollback capabilities' }
              ].map((item, index) => (
                <div key={index} className="text-center">
                  <div className={`w-12 h-12 bg-${item.color}-50 rounded-lg flex items-center justify-center mx-auto mb-4`}>
                    <div className={`w-6 h-6 flex items-center justify-center text-${item.color}-600`}>
                      <i className={`${item.icon} ri-lg`}></i>
                    </div>
                  </div>
                  <h5 className="font-semibold text-gray-900 mb-2">{item.title}</h5>
                  <p className="text-sm text-gray-600">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Consultation Form */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-6">Schedule Your Implementation Consultation</h2>
              <p className="text-xl text-gray-600">
                Get a personalized assessment of your enterprise software implementation needs. Our experts will analyze your requirements and provide a tailored solution roadmap.
              </p>
            </div>
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8">
              {formSubmitted ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-6">
                    <div className="w-8 h-8 flex items-center justify-center text-green-600">
                      <i className="ri-check-line ri-2x"></i>
                    </div>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">Thank You!</h3>
                  <p className="text-lg text-gray-600 mb-6">
                    Your consultation request has been submitted successfully. Our team will contact you within 24 hours.
                  </p>
                  <button
                    onClick={() => setFormSubmitted(false)}
                    className="text-primary hover:text-primary/80 font-medium cursor-pointer"
                  >
                    Submit Another Request
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6" data-readdy-form id="consultation-form">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">First Name *</label>
                      <input
                        name="first_name"
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded focus:ring-2 focus:ring-primary focus:border-primary text-sm"
                        placeholder="Enter your first name"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Last Name *</label>
                      <input
                        name="last_name"
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded focus:ring-2 focus:ring-primary focus:border-primary text-sm"
                        placeholder="Enter your last name"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Email Address *</label>
                      <input
                        type="email"
                        name="email"
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded focus:ring-2 focus:ring-primary focus:border-primary text-sm"
                        placeholder="Enter your email address"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number *</label>
                      <input
                        type="tel"
                        name="phone"
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded focus:ring-2 focus:ring-primary focus:border-primary text-sm"
                        placeholder="Enter your phone number"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Company Name *</label>
                      <input
                        name="company"
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded focus:ring-2 focus:ring-primary focus:border-primary text-sm"
                        placeholder="Enter your company name"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Job Title</label>
                      <input
                        name="job_title"
                        className="w-full px-4 py-3 border border-gray-300 rounded focus:ring-2 focus:ring-primary focus:border-primary text-sm"
                        placeholder="Enter your job title"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Company Size</label>
                      <div className="relative">
                        <button
                          type="button"
                          onClick={() => toggleDropdown('company-size')}
                          className="w-full px-4 py-3 border border-gray-300 rounded focus:ring-2 focus:ring-primary focus:border-primary text-sm text-left bg-white flex justify-between items-center cursor-pointer"
                        >
                          <span className={selectedOptions['company-size'] ? 'text-gray-900' : 'text-gray-500'}>
                            {selectedOptions['company-size'] || 'Select company size'}
                          </span>
                          <div className="w-4 h-4 flex items-center justify-center text-gray-400">
                            <i className="ri-arrow-down-s-line"></i>
                          </div>
                        </button>
                        {openDropdown === 'company-size' && (
                          <div className="absolute top-full left-0 right-0 bg-white border border-gray-300 rounded-md mt-1 z-10 shadow-lg">
                            <div className="py-1">
                              {['1-50 employees', '51-200 employees', '201-1000 employees', '1000+ employees'].map((option) => (
                                <button
                                  key={option}
                                  type="button"
                                  onClick={() => selectOption('company-size', option)}
                                  className="w-full px-4 py-2 text-left hover:bg-gray-100 text-sm cursor-pointer"
                                >
                                  {option}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Industry</label>
                      <div className="relative">
                        <button
                          type="button"
                          onClick={() => toggleDropdown('industry')}
                          className="w-full px-4 py-3 border border-gray-300 rounded focus:ring-2 focus:ring-primary focus:border-primary text-sm text-left bg-white flex justify-between items-center cursor-pointer"
                        >
                          <span className={selectedOptions['industry'] ? 'text-gray-900' : 'text-gray-500'}>
                            {selectedOptions['industry'] || 'Select industry'}
                          </span>
                          <div className="w-4 h-4 flex items-center justify-center text-gray-400">
                            <i className="ri-arrow-down-s-line"></i>
                          </div>
                        </button>
                        {openDropdown === 'industry' && (
                          <div className="absolute top-full left-0 right-0 bg-white border border-gray-300 rounded-md mt-1 z-10 shadow-lg">
                            <div className="py-1">
                              {['Manufacturing', 'Healthcare', 'Financial Services', 'Retail', 'Technology', 'Other'].map((option) => (
                                <button
                                  key={option}
                                  type="button"
                                  onClick={() => selectOption('industry', option)}
                                  className="w-full px-4 py-2 text-left hover:bg-gray-100 text-sm cursor-pointer"
                                >
                                  {option}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Current Software Environment</label>
                    <textarea
                      name="current_software"
                      rows={3}
                      maxLength={500}
                      className="w-full px-4 py-3 border border-gray-300 rounded focus:ring-2 focus:ring-primary focus:border-primary text-sm"
                      placeholder="Describe your current software systems and infrastructure"
                    ></textarea>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Implementation Requirements</label>
                    <textarea
                      name="requirements"
                      rows={4}
                      maxLength={500}
                      className="w-full px-4 py-3 border border-gray-300 rounded focus:ring-2 focus:ring-primary focus:border-primary text-sm"
                      placeholder="Describe your implementation goals, challenges, and specific requirements"
                    ></textarea>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Preferred Timeline</label>
                      <div className="relative">
                        <button
                          type="button"
                          onClick={() => toggleDropdown('timeline')}
                          className="w-full px-4 py-3 border border-gray-300 rounded focus:ring-2 focus:ring-primary focus:border-primary text-sm text-left bg-white flex justify-between items-center cursor-pointer"
                        >
                          <span className={selectedOptions['timeline'] ? 'text-gray-900' : 'text-gray-500'}>
                            {selectedOptions['timeline'] || 'Select timeline'}
                          </span>
                          <div className="w-4 h-4 flex items-center justify-center text-gray-400">
                            <i className="ri-arrow-down-s-line"></i>
                          </div>
                        </button>
                        {openDropdown === 'timeline' && (
                          <div className="absolute top-full left-0 right-0 bg-white border border-gray-300 rounded-md mt-1 z-10 shadow-lg">
                            <div className="py-1">
                              {['ASAP (1-3 months)', '3-6 months', '6-12 months', '12+ months'].map((option) => (
                                <button
                                  key={option}
                                  type="button"
                                  onClick={() => selectOption('timeline', option)}
                                  className="w-full px-4 py-2 text-left hover:bg-gray-100 text-sm cursor-pointer"
                                >
                                  {option}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Budget Range</label>
                      <div className="relative">
                        <button
                          type="button"
                          onClick={() => toggleDropdown('budget')}
                          className="w-full px-4 py-3 border border-gray-300 rounded focus:ring-2 focus:ring-primary focus:border-primary text-sm text-left bg-white flex justify-between items-center cursor-pointer"
                        >
                          <span className={selectedOptions['budget'] ? 'text-gray-900' : 'text-gray-500'}>
                            {selectedOptions['budget'] || 'Select budget range'}
                          </span>
                          <div className="w-4 h-4 flex items-center justify-center text-gray-400">
                            <i className="ri-arrow-down-s-line"></i>
                          </div>
                        </button>
                        {openDropdown === 'budget' && (
                          <div className="absolute top-full left-0 right-0 bg-white border border-gray-300 rounded-md mt-1 z-10 shadow-lg">
                            <div className="py-1">
                              {['Under $50K', '$50K - $100K', '$100K - $250K', '$250K+'].map((option) => (
                                <button
                                  key={option}
                                  type="button"
                                  onClick={() => selectOption('budget', option)}
                                  className="w-full px-4 py-2 text-left hover:bg-gray-100 text-sm cursor-pointer"
                                >
                                  {option}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <input
                      type="checkbox"
                      id="consent"
                      name="consent"
                      required
                      className="mt-1 cursor-pointer"
                    />
                    <label htmlFor="consent" className="ml-3 text-sm text-gray-600">
                      I agree to receive communications about enterprise software implementation services and consent to the processing of my personal data in accordance with the{' '}
                      <a href="#" className="text-primary hover:underline">Privacy Policy</a>.
                    </label>
                  </div>
                  <div className="text-center">
                    <button
                      type="submit"
                      className="bg-primary text-white px-8 py-4 rounded-button whitespace-nowrap font-semibold text-lg hover:bg-primary/90 transition-colors inline-flex items-center cursor-pointer"
                    >
                      <div className="w-5 h-5 flex items-center justify-center mr-2">
                        <i className="ri-calendar-check-line"></i>
                      </div>
                      Schedule Consultation
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">Frequently Asked Questions</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Get answers to common questions about our enterprise software implementation services and processes.
            </p>
          </div>
          <div className="max-w-4xl mx-auto space-y-6">
            {[
              {
                id: 'faq-1',
                question: 'How long does a typical enterprise software implementation take?',
                answer: 'Implementation timelines vary based on project complexity, system size, and customization requirements. Typical projects range from 3-18 months. Small to medium implementations usually take 3-6 months, while large enterprise deployments can take 12-18 months. We provide detailed timeline estimates during our initial assessment phase.'
              },
              {
                id: 'faq-2',
                question: 'What is included in your implementation services?',
                answer: 'Our comprehensive services include: system assessment and planning, architecture design, custom development and configuration, data migration, integration with existing systems, rigorous testing, user training, go-live support, and post-implementation optimization. We also provide ongoing maintenance and support options.'
              },
              {
                id: 'faq-3',
                question: 'How do you minimize business disruption during implementation?',
                answer: 'We use phased deployment strategies, parallel system operation, and off-hours implementation schedules to minimize disruption. Our team conducts thorough testing in staging environments, provides comprehensive rollback procedures, and maintains 24/7 support during critical deployment phases to ensure business continuity.'
              },
              {
                id: 'faq-4',
                question: 'What types of enterprise software do you implement?',
                answer: 'We specialize in ERP systems (SAP, Oracle, Microsoft Dynamics), CRM platforms (Salesforce, HubSpot), HR management systems (Workday, SuccessFactors), financial management software, supply chain management systems, and custom enterprise applications. Our team has expertise across multiple platforms and industries.'
              },
              {
                id: 'faq-5',
                question: 'Do you provide training for our team?',
                answer: 'Yes, comprehensive training is included in all our packages. We provide role-based training programs, administrator training, end-user workshops, and create custom training materials. Our training approach includes hands-on sessions, documentation, video tutorials, and ongoing support to ensure successful user adoption.'
              },
              {
                id: 'faq-6',
                question: 'What kind of post-implementation support do you offer?',
                answer: 'We offer tiered support packages including 24/7 technical support, system monitoring, performance optimization, regular health checks, security updates, user support, and system enhancements. Support duration ranges from 30 days to 12+ months depending on your selected package, with options for extended maintenance contracts.'
              }
            ].map((faq) => (
              <div key={faq.id} className="bg-gray-50 rounded-lg p-6">
                <button
                  onClick={() => toggleFAQ(faq.id)}
                  className="w-full text-left flex justify-between items-center cursor-pointer"
                >
                  <h3 className="text-lg font-semibold text-gray-900">{faq.question}</h3>
                  <div className="w-6 h-6 flex items-center justify-center text-gray-400">
                    <i className={`ri-${openFAQ === faq.id ? 'subtract' : 'add'}-line`}></i>
                  </div>
                </button>
                {openFAQ === faq.id && (
                  <div className="mt-4 text-gray-600">{faq.answer}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-primary text-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-6">Ready to Transform Your Business?</h2>
            <p className="text-xl text-blue-100 max-w-3xl mx-auto">
              Contact our enterprise software implementation experts today to discuss your project requirements and get a customized solution proposal.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            <div className="text-center">
              <div className="w-16 h-16 bg-white/10 rounded-xl flex items-center justify-center mx-auto mb-6">
                <div className="w-8 h-8 flex items-center justify-center text-white">
                  <i className="ri-phone-line ri-2x"></i>
                </div>
              </div>
              <h3 className="text-xl font-semibold mb-2">Call Us</h3>
              <p className="text-blue-100 mb-4">Speak with our implementation specialists</p>
              <a href="tel:+31616300011" className="text-white font-semibold hover:text-blue-200">
                +31 (0)616300011
              </a>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-white/10 rounded-xl flex items-center justify-center mx-auto mb-6">
                <div className="w-8 h-8 flex items-center justify-center text-white">
                  <i className="ri-mail-line ri-2x"></i>
                </div>
              </div>
              <h3 className="text-xl font-semibold mb-2">Email Us</h3>
              <p className="text-blue-100 mb-4">Get detailed information via email</p>
              <a href="mailto:sales@thyamis.com" className="text-white font-semibold hover:text-blue-200">
                sales@thyamis.com
              </a>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-white/10 rounded-xl flex items-center justify-center mx-auto mb-6">
                <div className="w-8 h-8 flex items-center justify-center text-white">
                  <i className="ri-calendar-line ri-2x"></i>
                </div>
              </div>
              <h3 className="text-xl font-semibold mb-2">Schedule Meeting</h3>
              <p className="text-blue-100 mb-4">Book a consultation session</p>
              <a
                href="#"
                className="bg-white text-primary px-6 py-2 rounded-button whitespace-nowrap font-semibold hover:bg-blue-50 transition-colors inline-block cursor-pointer"
              >
                Book Now
              </a>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
